// --------------------------------------------------------------
// Created On: 2021-09-01
// Author: Zachary Thomas
//
// Last Modified: 2026-05-07
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment } from "react";
import SuccessStatusIcon from "../SuccessStatusIcon/SuccessStatusIcon";
import IconTooltip from "../IconTooltip/IconTooltip";
import { TOOLTIPS } from "../../constants/tooltips";

// Display an assets diagnosis status (wireless connection, modbus, sensors, etc...).
export default function DisplayDiagnosisStatus(props: Props): Component {
  const anAnalogSensorIsDisconnected = props.analogSensors.some((analogSensor) => !analogSensor.connected);
  return (props.deviceShouldReport || props.controllerShouldReport || props.analogSensors.length > 0) &&
    !props.loading ? (
    <div className="my-3 text-center">
      {props.deviceShouldReport && (
        <Fragment>
          <div>
            Monitoring device established wireless connection{" "}
            <IconTooltip
              id="diagnosis-status-device-connected"
              icon="info-circle"
              color="var(--info-tooltip)"
              message={TOOLTIPS[props.deviceType as keyof typeof TOOLTIPS].copilotDeviceConnected}
            />
            : <SuccessStatusIcon loading={false} successful={props.deviceConnected} />
          </div>
          <div>
            Monitoring device is using latest firmware{" "}
            <IconTooltip
              id="diagnosis-status-device-on-latest-firmware"
              icon="info-circle"
              color="var(--info-tooltip)"
              message={TOOLTIPS[props.deviceType as keyof typeof TOOLTIPS].copilotLatestFirmware}
            />
            : <SuccessStatusIcon loading={false} successful={props.deviceIsOnLatestFirmware} />
          </div>
        </Fragment>
      )}
      {props.controllerShouldReport && (
        <div>
          Monitoring device properly connected to controller{" "}
          <IconTooltip
            id="diagnosis-status-controller-connected"
            icon="info-circle"
            color="var(--info-tooltip)"
            message={TOOLTIPS[props.deviceType as keyof typeof TOOLTIPS].copilotControllerConnected}
          />
          : <SuccessStatusIcon loading={false} successful={props.controllerConnected} />
        </div>
      )}
      {props.analogSensors.length > 0 && (
        <Fragment>
          <div>
            Analog sensors connected to monitoring device{" "}
            <IconTooltip
              id="diagnosis-status-analog-sensors-connected"
              icon="info-circle"
              color="var(--info-tooltip)"
              message={TOOLTIPS[props.deviceType as keyof typeof TOOLTIPS].copilotAnalogSensorsConnected}
            />
            : <SuccessStatusIcon loading={false} successful={!anAnalogSensorIsDisconnected} />
          </div>
          <div className="mx-3">
            {props.analogSensors.map((analogSensor) => (
              <div key={analogSensor.position}>
                Analog sensor #{analogSensor.position} connected:{" "}
                <SuccessStatusIcon loading={false} successful={analogSensor.connected} />
              </div>
            ))}
          </div>
        </Fragment>
      )}
    </div>
  ) : (
    <div>Monitoring device does not support wireless diagnosis.</div>
  );
}

interface Props {
  loading: boolean;
  deviceType: string | null;
  deviceShouldReport: boolean;
  deviceConnected: boolean;
  deviceIsOnLatestFirmware: boolean;
  controllerShouldReport: boolean;
  controllerConnected: boolean;
  analogSensors: AnalogSensor[];
}

interface AnalogSensor {
  position: number;
  connected: boolean;
}
